// Vendored from quill-languagetool v1.0.7 — adapted for Quill v2
// Key change: use Quill v2's Delta import instead of separate quill-delta package

import debug from "./debug";
import type Quill from "quill";
import type { QuillLanguageTool } from "./index";

/**
 * Clean all suggestion boxes from an HTML string.
 *
 * @param html HTML to clean
 * @returns Cleaned text
 */
export function getCleanedHtml(html: string) {
  return html.replace(/<quill-lt-match .+?>(.*?)<\/quill-lt-match>/g, "$1");
}

/**
 * Remove all suggestion boxes from the editor.
 */
export function removeSuggestionBoxes(quillEditor: Quill) {
  debug("Removing suggestion boxes for editor", quillEditor);

  const initialSelection = quillEditor.getSelection();
  const deltas = quillEditor.getContents();

  const deltasWithoutSuggestionBoxes = deltas.ops.map((delta: any) => {
    if (delta.attributes && delta.attributes.ltmatch) {
      return {
        ...delta,
        attributes: {
          ...delta.attributes,
          ltmatch: null,
        },
      };
    }
    return delta;
  });

  // Use Quill's Delta constructor via the editor instance
  const Delta = (quillEditor as any).constructor.import("delta");
  quillEditor.setContents(new Delta(deltasWithoutSuggestionBoxes));

  if (initialSelection) {
    quillEditor.setSelection(initialSelection);
  }
}

/**
 * Manager for the suggestion boxes.
 * This handles inserting and removing suggestion box elements from the editor.
 */
export class SuggestionBoxes {
  constructor(private readonly parent: QuillLanguageTool) {}

  /**
   * Remove all suggestion boxes from the editor.
   */
  public removeSuggestionBoxes() {
    this.parent.preventLoop();
    removeSuggestionBoxes(this.parent.quill);
  }

  /**
   * Insert suggestion boxes into the editor.
   * This uses the matches stored in the parent class.
   */
  public addSuggestionBoxes() {
    const Delta = (this.parent.quill as any).constructor.import("delta");

    this.parent.matches.forEach((match) => {
      this.parent.preventLoop();

      const ops = new Delta().retain(match.offset).retain(match.length, { ltmatch: match });
      this.parent.quill.updateContents(ops, "api");

      debug("Adding formatter", "lt-match", match.offset, match.length);
    });
  }
}
